import { AppRoutes } from "@/shared/lib/routes"
import { createRoute } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import { rootRoute } from "./__root"
import { usePlans } from "@/features/products"
import { buildConsentDisclaimer, useUpsell } from "@/features/payments"
import {
  OfferSalesPage,
  resolveOfferEntries,
  OFFER_PRESETS,
  useOffersFunnelStore,
} from "@/features/offers"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import {
  buildAstroMapReportUser,
  buildLoveMatchReportUser,
  buildStarReportUser,
  requestAstroMapReport,
  requestLoveMatchReport,
  requestPdfReport,
} from "@/features/today"
import { buildAuraScanInput, requestAuraScanReport } from "@/features/aura-scan-report"
import { buildSoulmateInput, requestSoulmateReport } from "@/features/soulmate-report"
import { useQuizStore } from "@/stores/quiz-store"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { LoadingSpinner } from "@/shared/components/loading-spinner"
import { formatMoney } from "@/shared/lib/money"
import { showErrorToast } from "@/stores/toast-store"

export const additionalOffersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AppRoutes.ADDITIONAL_OFFERS,
  component: AdditionalOffersPage,
})

/**
 * Post-purchase additional-offers funnel. Offers come from config (one
 * `<offer>_report` param each); we show them one sales page at a time, charge
 * the card on file one-click on "Unlock", and advance. "Skip" moves on without
 * charging. After the last offer (or when none are configured) we hand off to
 * `/create-account`.
 */
function AdditionalOffersPage() {
  const navigate = useNavigateWithParams()
  // The current offer index lives in a session-persisted store (no URL param),
  // so a mid-funnel refresh resumes on the same offer. Only the final hand-off
  // to /create-account navigates.
  const step = useOffersFunnelStore((s) => s.step)
  const setStep = useOffersFunnelStore((s) => s.setStep)

  const entries = useMemo(() => resolveOfferEntries(), [])
  const planIds = useMemo(() => entries.map((e) => e.planId), [entries])
  const { plans } = usePlans(planIds)
  const { purchase, status: upsellStatus, canPurchase } = useUpsell()
  const quizAnswers = useQuizStore((s) => s.answers)

  const offerLabels = useMemo(
    () => entries.map((e) => OFFER_PRESETS[e.key]?.stepLabel ?? ""),
    [entries],
  )

  const done = entries.length === 0 || step >= entries.length
  const entry = done ? null : entries[step]
  const preset = entry ? OFFER_PRESETS[entry.key] : null
  const plan = entry ? plans.find((p) => p.id === entry.planId) : undefined

  // No offers (or past the last one) → straight to the account step.
  useEffect(() => {
    if (done) navigate({ to: AppRoutes.CREATE_ACCOUNT })
  }, [done, navigate])

  // Each offer starts at the top of the page.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [step])

  useEffect(() => {
    if (plan) {
      trackEvent(ANALYTICS_EVENTS.PURCHASE_PAGE_VIEWED, {
        productId: plan.id,
        price: plan.price,
        currency: plan.currency,
        metadata: { kind: "additional_offer" },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.id])

  if (done || !entry || !preset) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-dark-base">
        <LoadingSpinner />
      </div>
    )
  }

  const goNext = () => {
    if (step + 1 >= entries.length) navigate({ to: AppRoutes.CREATE_ACCOUNT })
    else setStep(step + 1)
  }

  const handleUnlock = async () => {
    // Nothing to charge → just move on. On success advance; on failure useUpsell
    // shows the error toast and we stay on this offer.
    if (!plan || !canPurchase) {
      goNext()
      return
    }
    const newSubscriptionId = await purchase(plan, { additionalOffer: true })
    if (!newSubscriptionId) return

    // Report offers: fire the (pre-account) report request with a minimal,
    // per-report snapshot from the quiz. Each report reads only its own fields,
    // so we build a dedicated payload per offer and omit anything the backend
    // doesn't use — no report depends on a `name` the astro-map / soulmate
    // funnels never collect. All five need a subscription id + email; the rest
    // is report-specific. Best-effort — a failure toasts but never blocks
    // advancing.
    const email = typeof quizAnswers.email === "string" ? quizAnswers.email.trim() : ""
    if (email) {
      try {
        if (entry.key === "astrology") {
          const user = buildStarReportUser(quizAnswers)
          if (user) await requestPdfReport({ user, email, subscriptionId: newSubscriptionId })
        } else if (entry.key === "compatibility") {
          await requestLoveMatchReport({
            user: buildLoveMatchReportUser(quizAnswers),
            email,
            subscriptionId: newSubscriptionId,
          })
        } else if (entry.key === "astromap") {
          const user = buildAstroMapReportUser(quizAnswers)
          if (user) await requestAstroMapReport({ user, email, subscriptionId: newSubscriptionId })
        } else if (entry.key === "soulmate") {
          // Partner fields are only present on the soulmate quiz; other funnels
          // send just gender + dob.
          const user = buildSoulmateInput(quizAnswers)
          if (user) await requestSoulmateReport({ user, email, subscriptionId: newSubscriptionId })
        } else if (entry.key === "aura") {
          const user = buildAuraScanInput(quizAnswers)
          if (user) await requestAuraScanReport({ user, email, subscriptionId: newSubscriptionId })
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Could not request your report."
        console.error(`${entry.key} report request failed:`, err)
        showErrorToast("Couldn't request your report", msg)
      }
    }

    goNext()
  }

  const reportNow = plan ? formatMoney(plan.price, plan.currency) : ""
  const reportWas = plan?.previousPrice ? formatMoney(plan.previousPrice, plan.currency) : undefined

  // Show the money-back + consent disclaimer only when the offer is a recurring
  // subscription (backend `one_time === false` → interval isn't one_time/lifetime).
  const isSubscription = Boolean(
    plan && plan.interval !== "one_time" && plan.interval !== "lifetime",
  )
  // Plan-aware consent copy so a subscription offer states its real trial/rebill
  // terms (shared with the main checkout) instead of a fixed one-time line.
  const consentText = plan ? buildConsentDisclaimer(plan) : undefined

  return (
    <OfferSalesPage
      preset={preset}
      offerLabels={offerLabels}
      currentIndex={step}
      reportWas={reportWas}
      reportNow={reportNow}
      purchasing={upsellStatus === "processing"}
      showDisclaimer={isSubscription}
      consentText={consentText}
      onUnlock={handleUnlock}
      onSkip={goNext}
    />
  )
}
